import React, { useEffect, useState } from "react";
import { X, Download, Share } from "lucide-react";

/**
 * Banner "Aggiungi a schermata Home".
 *
 * ANDROID: intercetta `beforeinstallprompt` e mostra bottone "Installa"
 *          che chiama prompt() sull'evento salvato.
 * iOS:     Apple non espone beforeinstallprompt, quindi mostriamo un
 *          banner informativo con l'istruzione "Tocca Condividi ->
 *          Aggiungi a schermata Home" solo in Safari e solo se non
 *          siamo gia in standalone.
 *
 * Non viene mostrato nulla su desktop.
 * Il dismiss manuale viene ricordato per 7 giorni in localStorage
 * (chiavi separate per Android e iOS).
 */
const DISMISS_KEY_ANDROID = "ht_pwa_install_dismissed_at";
const DISMISS_KEY_IOS = "ht_pwa_install_dismissed_at_ios";
const DISMISS_DAYS = 7;

function isAndroidMobile() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /Android/i.test(ua) && /Mobile/i.test(ua);
}

function isIOSSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  if (!isIOS) return false;
  // Escludi Chrome iOS (CriOS), Firefox iOS (FxiOS), Edge iOS (EdgiOS)
  // perche su iOS solo Safari puo installare la PWA.
  if (/CriOS|FxiOS|EdgiOS|OPiOS|mercury/i.test(ua)) return false;
  return true;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  const mql = window.matchMedia && window.matchMedia("(display-mode: standalone)");
  if (mql && mql.matches) return true;
  if (window.navigator && window.navigator.standalone) return true;
  return false;
}

function recentlyDismissed(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!ts) return false;
    const diffDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return diffDays < DISMISS_DAYS;
  } catch (_) {
    return false;
  }
}

export default function PWAInstallBanner() {
  const [deferred, setDeferred] = useState(null);
  const [androidVisible, setAndroidVisible] = useState(false);
  const [iosVisible, setIosVisible] = useState(false);
  const [iosShowSteps, setIosShowSteps] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;

    if (isAndroidMobile()) {
      if (recentlyDismissed(DISMISS_KEY_ANDROID)) return;
      const onPrompt = (e) => {
        e.preventDefault();
        setDeferred(e);
        setAndroidVisible(true);
      };
      const onInstalled = () => {
        setAndroidVisible(false);
        setDeferred(null);
      };
      window.addEventListener("beforeinstallprompt", onPrompt);
      window.addEventListener("appinstalled", onInstalled);
      return () => {
        window.removeEventListener("beforeinstallprompt", onPrompt);
        window.removeEventListener("appinstalled", onInstalled);
      };
    }

    if (isIOSSafari()) {
      if (recentlyDismissed(DISMISS_KEY_IOS)) return;
      // Piccolo delay cosi non appare subito all'ingresso in pagina.
      const t = setTimeout(() => setIosVisible(true), 1500);
      return () => clearTimeout(t);
    }
  }, []);

  const handleInstall = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice && choice.outcome === "dismissed") {
        try { localStorage.setItem(DISMISS_KEY_ANDROID, String(Date.now())); } catch (_) {}
      }
    } catch (_) {}
    setAndroidVisible(false);
    setDeferred(null);
  };

  const handleDismissAndroid = () => {
    try { localStorage.setItem(DISMISS_KEY_ANDROID, String(Date.now())); } catch (_) {}
    setAndroidVisible(false);
  };

  const handleDismissIos = () => {
    try { localStorage.setItem(DISMISS_KEY_IOS, String(Date.now())); } catch (_) {}
    setIosVisible(false);
  };

  if (androidVisible) {
    return (
      <div
        data-testid="pwa-install-banner"
        className="fixed bottom-0 inset-x-0 z-[1100] px-3 pb-3 pointer-events-none"
      >
        <div className="pointer-events-auto mx-auto max-w-md rounded-xl shadow-2xl border border-slate-200 bg-white p-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-600 text-white flex items-center justify-center shrink-0">
            <Download className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate">
              Installa Housetag sul tuo telefono
            </div>
            <div className="text-xs text-slate-500 truncate">
              Accedi piu veloce dalla schermata Home
            </div>
          </div>
          <button
            data-testid="pwa-install-btn"
            onClick={handleInstall}
            className="shrink-0 inline-flex items-center justify-center gap-1 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-medium text-sm px-3 py-2"
          >
            Installa
          </button>
          <button
            data-testid="pwa-install-dismiss-btn"
            onClick={handleDismissAndroid}
            aria-label="Chiudi"
            className="shrink-0 rounded-lg p-2 text-slate-500 hover:bg-slate-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  if (iosVisible) {
    return (
      <div
        data-testid="pwa-install-banner-ios"
        className="fixed bottom-0 inset-x-0 z-[1100] px-3 pb-3 pointer-events-none"
      >
        <div className="pointer-events-auto mx-auto max-w-md rounded-xl shadow-2xl border border-slate-200 bg-white p-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center shrink-0">
              <Share className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900">
                Aggiungi Housetag alla schermata Home
              </div>
              <div className="text-xs text-slate-600 mt-0.5">
                Tocca <Share className="w-3 h-3 inline-block mx-0.5 -mt-0.5" /> Condividi, poi <strong>Aggiungi a schermata Home</strong>.
              </div>
              {iosShowSteps && (
                <ol
                  data-testid="pwa-install-ios-steps"
                  className="list-decimal pl-4 mt-2 text-xs text-slate-700 space-y-0.5"
                >
                  <li>Tocca l'icona Condividi in basso in Safari</li>
                  <li>Scorri e seleziona <strong>Aggiungi a schermata Home</strong></li>
                  <li>Conferma: trovi l'icona Housetag sul telefono</li>
                </ol>
              )}
              <button
                data-testid="pwa-install-ios-steps-btn"
                onClick={() => setIosShowSteps((v) => !v)}
                className="mt-2 text-xs font-medium text-purple-700 hover:text-purple-900 underline"
              >
                {iosShowSteps ? "Nascondi passi" : "Mostra i passi"}
              </button>
            </div>
            <button
              data-testid="pwa-install-ios-dismiss-btn"
              onClick={handleDismissIos}
              aria-label="Chiudi"
              className="shrink-0 rounded-lg p-2 text-slate-500 hover:bg-slate-100"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
